import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';

interface Message {
  id: string;
  lead_id?: string;
  client_id?: string;
  sender_type: 'lead' | 'system';
  content: string;
  created_at: string;
  read: boolean;
}

interface WhatsAppChatProps {
  leadId?: string;
  clientId?: string;
  contactName: string;
  phoneNumber: string;
}

export default function WhatsAppChat({ leadId, clientId, contactName, phoneNumber }: WhatsAppChatProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const entityKey = leadId ? 'lead_id' : 'client_id';
  const entityId = leadId || clientId;

  useEffect(() => {
    if (!entityId) return;

    loadMessages();

    // Subscribe to new and updated messages
    const channel = supabase
      .channel(`chat-${entityId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'lead_messages',
          filter: `${entityKey}=eq.${entityId}`,
        },
        (payload) => {
          const newMessage = payload.new as Message;
          setMessages((prev) => {
            if (prev.some((m) => m.id === newMessage.id)) return prev;
            return [...prev, newMessage];
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'lead_messages',
          filter: `${entityKey}=eq.${entityId}`,
        },
        (payload) => {
          const updatedMessage = payload.new as Message;
          setMessages((prev) =>
            prev.map((m) => (m.id === updatedMessage.id ? updatedMessage : m))
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [leadId, clientId, entityId, entityKey]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const loadMessages = async () => {
    if (!entityId) return;

    const { data } = await supabase
      .from('lead_messages')
      .select('*')
      .eq(entityKey, entityId)
      .order('created_at', { ascending: true });
    
    if (data) setMessages(data);
    setLoading(false);
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || sending || !entityId) return;

    const messageToSend = newMessage.trim();
    setNewMessage(''); // Clear early for better UX
    setSending(true);

    try {
      // 1. Get webhook URL
      const { data: config } = await supabase
        .from('webhook_configs')
        .select('webhook_url')
        .eq('event_name', 'send_message')
        .eq('is_active', true)
        .maybeSingle();

      if (!config?.webhook_url) {
        throw new Error('Configuração de webhook "send_message" não encontrada ou inativa.');
      }

      // 2. Save locally first to get the ID
      const { data: savedMsg, error: insertError } = await supabase
        .from('lead_messages')
        .insert({
          [entityKey]: entityId,
          sender_type: 'system',
          content: messageToSend,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // 3. Call external API with the message ID
      const response = await fetch(config.webhook_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contact_name: contactName,
          phone: phoneNumber,
          message: messageToSend,
          message_id: savedMsg.id, // ID da linha de registro criada
        }),
      });

      if (!response.ok) {
        throw new Error('Falha ao enviar mensagem via API do WhatsApp');
      }

    } catch (err: any) {
      console.error(err);
      setNewMessage(messageToSend); // Restore on error
      alert('Erro ao enviar mensagem: ' + err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col h-[500px] bg-[#f0f2f5] rounded-xl overflow-hidden border border-slate-200 shadow-sm">
      {/* Header */}
      <div className="bg-[#075e54] text-white px-4 py-3 flex items-center gap-3 shrink-0">
        <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center shrink-0">
          <i className="ri-whatsapp-line text-xl"></i>
        </div>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold truncate">{contactName || 'Contato sem nome'}</h3>
          <p className="text-[10px] opacity-80 truncate">{phoneNumber || 'Sem telefone'}</p>
        </div>
      </div>

      {/* Messages */}
      <div 
        ref={scrollRef}
        className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#e5ddd5]"
        style={{ 
          backgroundImage: 'linear-gradient(rgba(229, 221, 213, 0.9), rgba(229, 221, 213, 0.9)), url("https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png")',
          backgroundSize: '300px'
        }}
      >
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-slate-500 opacity-60 italic text-sm text-center px-4">
            <i className="ri-chat-history-line text-2xl mb-2"></i>
            Nenhuma mensagem registrada para este lead
          </div>
        ) : (
          messages.map((m) => (
            <div 
              key={m.id} 
              className={`flex ${m.sender_type === 'system' ? 'justify-end' : 'justify-start'}`}
            >
              <div 
                className={`max-w-[85%] px-3 py-1.5 rounded-lg text-[13px] shadow-sm relative ${
                  m.sender_type === 'system' 
                    ? 'bg-[#dcf8c6] text-slate-800 rounded-tr-none' 
                    : 'bg-white text-slate-800 rounded-tl-none'
                }`}
              >
                <p className="whitespace-pre-wrap leading-relaxed">{m.content}</p>
                <div className="flex items-center justify-end gap-1 mt-1">
                  <span className="text-[9px] text-slate-400">
                    {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  {m.sender_type === 'system' && (
                    <span className={m.read ? 'text-[#34b7f1]' : 'text-slate-400'}>
                      <i className={m.read ? 'ri-check-double-line' : 'ri-check-line'} style={{ fontSize: '14px' }}></i>
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Input */}
      <form onSubmit={handleSendMessage} className="bg-[#f0f2f5] p-3 flex items-center gap-2 shrink-0">
        <input 
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Digite uma mensagem..."
          className="flex-1 px-4 py-2 bg-white rounded-full border-none focus:ring-1 focus:ring-emerald-500 text-sm focus:outline-none"
        />
        <button 
          type="submit"
          disabled={!newMessage.trim() || sending}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-[#128c7e] text-white hover:bg-[#075e54] disabled:opacity-50 transition-all shadow-md active:scale-95 cursor-pointer shrink-0"
        >
          {sending ? (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
          ) : (
            <i className="ri-send-plane-2-fill text-lg"></i>
          )}
        </button>
      </form>
    </div>
  );
}
